/**
 * Untyped view of the shared Supabase client, for the Smart CMS tables.
 *
 * WHY THIS EXISTS: `supabase` is typed from `lib/agents/supabase.ts`, which is
 * GENERATED from the live database. The scms_* tables do not exist there until
 * the database migration has actually been run, so every call
 * against them fails to compile -- a chicken-and-egg problem, not a real type
 * error.
 *
 * Scoped deliberately:
 *   - It is the SAME client instance, so auth, headers and RLS are unchanged.
 *   - Only this module's callers lose table typing, not the rest of the app.
 *   - The row shapes are still checked, because the adapters map through
 *     explicit row-to-entity and entity-to-row functions rather than passing
 *     raw objects around.
 *
 * ONCE THE MIGRATIONS ARE APPLIED: regenerate the database types
 * (`supabase gen types typescript`) and this file can be deleted, with the
 * adapters importing the typed client directly.
 */

import { supabase } from "@/lib/data"

/**
 * Minimal structural type for the query builder we actually use. Deliberately
 * not `any` at the call sites -- this keeps `.from(...).select(...)` chains
 * checked for shape even though the table names are not in the generated union.
 */
interface UntypedQuery {
  select(columns?: string): UntypedQuery
  insert(values: unknown): UntypedQuery
  upsert(values: unknown, options?: { onConflict?: string }): UntypedQuery
  update(values: unknown): UntypedQuery
  delete(): UntypedQuery
  eq(column: string, value: unknown): UntypedQuery
  in(column: string, values: readonly unknown[]): UntypedQuery
  not(column: string, operator: string, value: unknown): UntypedQuery
  like(column: string, pattern: string): UntypedQuery
  order(column: string, options?: { ascending?: boolean }): UntypedQuery
  limit(count: number): UntypedQuery
  range(from: number, to: number): UntypedQuery
  single(): PromiseLike<{ data: any; error: any }>
  then<R1 = { data: any[] | null; error: any }, R2 = never>(
    onfulfilled?: ((value: { data: any[] | null; error: any }) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: any) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2>
}

/** The shared client, with table-name typing relaxed. Same instance, same auth. */
export const cmsDb = {
  from(table: string): UntypedQuery {
    return (supabase as any).from(table) as UntypedQuery
  },
  rpc(fn: string, args?: Record<string, unknown>): PromiseLike<{ data: any; error: any }> {
    return (supabase as any).rpc(fn, args)
  },
}
